import React, { useMemo, useState, useRef } from 'react';
import { useStore, MenuItem } from '../hooks/useStore';
import { formatPrice } from '../lib/formatPrice';
import { Trash2, Edit2, Plus, Save, Check, LayoutList, UtensilsCrossed, Search, EyeOff, GripVertical } from 'lucide-react';
export function MenuEditorPage() {
  const {
    menuItems,
    categories,
    addMenuItem,
    updateMenuItem,
    deleteMenuItem,
    reorderItems,
    reorderCategories,
    addCategory,
    deleteCategory
  } = useStore();
  const [activeTab, setActiveTab] = useState<'categories' | 'items'>('categories');
  // ── STATS ──
  const stats = useMemo(() => ({
    totalItems: menuItems.length,
    totalCategories: categories.length,
    unavailableItems: menuItems.filter(i => !i.available).length
  }), [menuItems, categories]);
  // ── CATEGORIES STATE ──
  const [newCategoryName, setNewCategoryName] = useState('');
  const [deletingCategoryName, setDeletingCategoryName] = useState<string | null>(null);
  // ── CATEGORY DRAG STATE ──
  const catDragIndex = useRef<number | null>(null);
  const [catDragOver, setCatDragOver] = useState<number | null>(null);
  const handleCatDragStart = (idx: number) => {
    catDragIndex.current = idx;
  };
  const handleCatDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setCatDragOver(idx);
  };
  const handleCatDrop = (e: React.DragEvent, dropIdx: number) => {
    e.preventDefault();
    const from = catDragIndex.current;
    if (from === null || from === dropIdx) {
      setCatDragOver(null);
      return;
    }
    const newCats = [...categories];
    const [moved] = newCats.splice(from, 1);
    newCats.splice(dropIdx, 0, moved);
    reorderCategories(newCats);
    catDragIndex.current = null;
    setCatDragOver(null);
  };
  const handleCatDragEnd = () => {
    catDragIndex.current = null;
    setCatDragOver(null);
  };
  // ── ITEMS STATE ──
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('Todos');
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isCreatingItem, setIsCreatingItem] = useState(false);
  const [activeCategoryForNewItem, setActiveCategoryForNewItem] = useState<string | null>(null);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  // ── ITEM DRAG STATE (per category) ──
  const itemDragRef = useRef<{
    category: string;
    id: string;
  } | null>(null);
  const [itemDragOver, setItemDragOver] = useState<{
    category: string;
    id: string;
  } | null>(null);
  const handleItemDragStart = (category: string, id: string) => {
    itemDragRef.current = {
      category,
      id
    };
  };
  const handleItemDragOver = (e: React.DragEvent, category: string, id: string) => {
    e.preventDefault();
    if (itemDragRef.current?.category !== category) return;
    setItemDragOver({
      category,
      id
    });
  };
  const handleItemDrop = (e: React.DragEvent, dropCategory: string, dropId: string) => {
    e.preventDefault();
    const drag = itemDragRef.current;
    if (!drag || drag.category !== dropCategory || drag.id === dropId) {
      setItemDragOver(null);
      return;
    }
    const catItems = menuItems.filter(i => i.category === dropCategory).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const fromIdx = catItems.findIndex(i => i.id === drag.id);
    const toIdx = catItems.findIndex(i => i.id === dropId);
    if (fromIdx === -1 || toIdx === -1) return;
    const newItems = [...catItems];
    const [moved] = newItems.splice(fromIdx, 1);
    newItems.splice(toIdx, 0, moved);
    reorderItems(dropCategory, newItems.map(i => i.id));
    itemDragRef.current = null;
    setItemDragOver(null);
  };
  const handleItemDragEnd = () => {
    itemDragRef.current = null;
    setItemDragOver(null);
  };
  const [itemFormData, setItemFormData] = useState<Omit<MenuItem, 'id' | 'sort_order'>>({
    name: '',
    description: '',
    price: 0,
    category: '',
    available: true
  });
  const handleSaveItem = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingItemId) {
      updateMenuItem(editingItemId, itemFormData);
      setEditingItemId(null);
    } else {
      addMenuItem(itemFormData);
      setIsCreatingItem(false);
      setActiveCategoryForNewItem(null);
    }
    resetItemForm();
  };
  const resetItemForm = () => {
    setItemFormData({
      name: '',
      description: '',
      price: 0,
      category: '',
      available: true
    });
  };
  const startEditItem = (item: MenuItem) => {
    setItemFormData({
      name: item.name,
      description: item.description,
      price: item.price,
      category: item.category,
      available: item.available
    });
    setEditingItemId(item.id);
    setIsCreatingItem(false);
    setDeletingItemId(null);
  };
  const startCreateItem = (category: string) => {
    resetItemForm();
    setItemFormData(prev => ({
      ...prev,
      category
    }));
    setActiveCategoryForNewItem(category);
    setIsCreatingItem(true);
    setEditingItemId(null);
  };
  const filteredItems = useMemo(() => {
    let items = menuItems;
    if (selectedCategoryFilter !== 'Todos') {
      items = items.filter(i => i.category === selectedCategoryFilter);
    }
    if (itemSearchQuery) {
      const q = itemSearchQuery.toLowerCase();
      items = items.filter(i => i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
    }
    return items;
  }, [menuItems, selectedCategoryFilter, itemSearchQuery]);
  const groupedItems = useMemo(() => {
    const groups: {
      category: string;
      items: MenuItem[];
    }[] = [];
    categories.forEach(cat => {
      const catItems = filteredItems.filter(i => i.category === cat).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      if (catItems.length > 0 || selectedCategoryFilter === cat && !itemSearchQuery) {
        groups.push({
          category: cat,
          items: catItems
        });
      }
    });
    return groups;
  }, [categories, filteredItems, selectedCategoryFilter, itemSearchQuery]);
  return <div className="min-h-screen bg-[#0a0a0a] text-stone-200 font-sans pb-20" data-id="element-372">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-6 sm:py-8" data-id="element-373">
        {/* ── HEADER ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8" data-id="element-374">
          <div data-id="element-375">
            <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight" data-id="element-376">
              Editor de Menú
            </h1>
            <p className="text-stone-500 text-sm mt-1" data-id="element-377">
              Gestiona tu carta y categorías
            </p>
          </div>
          <div className="flex bg-stone-900 p-1 rounded-lg border border-stone-800 self-start sm:self-auto" data-id="element-378">
            <button onClick={() => setActiveTab('categories')} className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'categories' ? 'bg-stone-800 text-white shadow-sm' : 'text-stone-500 hover:text-stone-300'}`} data-id="element-379">
              <LayoutList className="w-4 h-4" data-id="element-380" />
              <span className="hidden xs:inline" data-id="element-381">Categorías</span>
            </button>
            <button onClick={() => setActiveTab('items')} className={`px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'items' ? 'bg-stone-800 text-white shadow-sm' : 'text-stone-500 hover:text-stone-300'}`} data-id="element-382">
              <UtensilsCrossed className="w-4 h-4" data-id="element-383" />
              <span className="hidden xs:inline" data-id="element-384">Platos</span>
            </button>
          </div>
        </div>

        {/* ── STATS BAR ── */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8" data-id="element-385">
          {[{
          label: 'Total Platos',
          value: stats.totalItems,
          icon: <UtensilsCrossed className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-386" />
        }, {
          label: 'Categorías',
          value: stats.totalCategories,
          icon: <LayoutList className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-387" />
        }, {
          label: 'No Disponibles',
          value: stats.unavailableItems,
          icon: <EyeOff className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-388" />
        }].map(({
          label,
          value,
          icon
        }) => <div key={label} className="bg-stone-900 border border-stone-800 rounded-xl p-3 sm:p-5 flex items-center justify-between" data-id="element-389">
              <div data-id="element-390">
                <p className="text-stone-500 text-[10px] sm:text-xs font-bold uppercase tracking-wider leading-tight" data-id="element-391">
                  {label}
                </p>
                <p className="text-2xl sm:text-3xl font-bold text-white mt-1" data-id="element-392">
                  {value}
                </p>
              </div>
              <div className="bg-stone-800 p-2 sm:p-3 rounded-lg text-stone-400" data-id="element-393">
                {icon}
              </div>
            </div>)}
        </div>

        {/* ── CATEGORIES TAB ── */}
        {activeTab === 'categories' && <div className="space-y-6" data-id="element-394">
            <div className="bg-[#1c1917] border border-stone-800 rounded-xl overflow-hidden" data-id="element-395">
              <div className="px-4 sm:px-6 py-4 border-b border-stone-800 bg-stone-900/50 flex justify-between items-center" data-id="element-396">
                <h2 className="font-bold text-white flex items-center gap-2 text-sm sm:text-base" data-id="element-397">
                  <LayoutList className="w-4 h-4 text-amber-500" data-id="element-398" />
                  Orden de Categorías
                </h2>
                <span className="text-xs text-stone-500 font-mono" data-id="element-399">
                  {categories.length} total
                </span>
              </div>

              <div className="divide-y divide-stone-800" data-id="element-400">
                {categories.map((cat, idx) => <div key={cat} draggable onDragStart={() => handleCatDragStart(idx)} onDragOver={e => handleCatDragOver(e, idx)} onDrop={e => handleCatDrop(e, idx)} onDragEnd={handleCatDragEnd} className={`flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 transition-all cursor-grab active:cursor-grabbing ${catDragOver === idx ? 'bg-amber-500/10 border-l-2 border-amber-500' : catDragIndex.current === idx ? 'opacity-40' : 'hover:bg-stone-800/30'}`} data-id="element-401">
                    {/* Drag Handle */}
                    <div className="text-stone-600 hover:text-stone-400 flex-shrink-0 touch-none" data-id="element-402">
                      <GripVertical className="w-5 h-5" data-id="element-403" />
                    </div>

                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded bg-stone-800 flex items-center justify-center text-stone-500 font-mono text-xs flex-shrink-0" data-id="element-404">
                      {idx + 1}
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-405">
                      <h3 className="font-bold text-stone-200 text-sm sm:text-base truncate" data-id="element-406">
                        {cat}
                      </h3>
                      <p className="text-xs text-stone-500" data-id="element-407">
                        {menuItems.filter(i => i.category === cat).length}{' '}
                        platos
                      </p>
                    </div>

                    {deletingCategoryName === cat ? <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0" data-id="element-408">
                        <span className="text-xs text-red-400 font-bold hidden sm:inline" data-id="element-409">
                          ¿Eliminar?
                        </span>
                        <button onClick={() => setDeletingCategoryName(null)} className="px-2 sm:px-3 py-1.5 rounded-md border border-stone-700 text-stone-400 text-xs hover:bg-stone-800" data-id="element-410">
                          No
                        </button>
                        <button onClick={() => {
                  deleteCategory(cat);
                  setDeletingCategoryName(null);
                }} className="px-2 sm:px-3 py-1.5 rounded-md bg-red-900/20 text-red-400 border border-red-900/50 text-xs font-bold hover:bg-red-900/40" data-id="element-411">
                          Sí
                        </button>
                      </div> : <button onClick={() => setDeletingCategoryName(cat)} className="p-2 text-stone-600 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0" data-id="element-412">
                        <Trash2 className="w-4 h-4" data-id="element-413" />
                      </button>}
                  </div>)}
              </div>

              {/* Add Category */}
              <div className="p-4 sm:p-6 bg-stone-900/30 border-t border-stone-800" data-id="element-414">
                <form onSubmit={e => {
              e.preventDefault();
              if (newCategoryName.trim()) {
                addCategory(newCategoryName.trim());
                setNewCategoryName('');
              }
            }} className="flex gap-2 sm:gap-3" data-id="element-415">
                  <input type="text" placeholder="Nueva categoría..." value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} className="flex-1 bg-[#0a0a0a] border border-stone-800 rounded-lg px-3 sm:px-4 py-2.5 text-sm text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none placeholder:text-stone-600" data-id="element-416" />
                  <button type="submit" disabled={!newCategoryName.trim()} className="bg-white text-black font-bold px-3 sm:px-5 py-2.5 rounded-lg text-sm hover:bg-stone-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 flex-shrink-0" data-id="element-417">
                    <Plus className="w-4 h-4" data-id="element-418" />
                    <span className="hidden sm:inline" data-id="element-419">Agregar</span>
                  </button>
                </form>
              </div>
            </div>
          </div>}

        {/* ── ITEMS TAB ── */}
        {activeTab === 'items' && <div className="space-y-6" data-id="element-420">
            {/* Filters */}
            <div className="space-y-3" data-id="element-421">
              <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{
            scrollbarWidth: 'none'
          }} data-id="element-422">
                <button onClick={() => setSelectedCategoryFilter('Todos')} className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors flex-shrink-0 ${selectedCategoryFilter === 'Todos' ? 'bg-amber-500 text-black' : 'bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-stone-200'}`} data-id="element-423">
                  Todos
                </button>
                {categories.map(cat => <button key={cat} onClick={() => setSelectedCategoryFilter(cat)} className={`px-3 sm:px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-colors flex-shrink-0 ${selectedCategoryFilter === cat ? 'bg-amber-500 text-black' : 'bg-stone-800 text-stone-400 hover:bg-stone-700 hover:text-stone-200'}`} data-id="element-424">
                    {cat}
                  </button>)}
              </div>

              <div className="relative" data-id="element-425">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" data-id="element-426" />
                <input type="text" placeholder="Buscar plato..." value={itemSearchQuery} onChange={e => setItemSearchQuery(e.target.value)} className="w-full bg-[#1c1917] border border-stone-800 rounded-xl pl-10 pr-4 py-2.5 sm:py-3 text-sm text-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none placeholder:text-stone-600" data-id="element-427" />
              </div>
            </div>

            {/* Groups */}
            <div className="space-y-6 sm:space-y-8" data-id="element-428">
              {groupedItems.map(({
            category,
            items: catItems
          }) => {
            const isAddingHere = isCreatingItem && activeCategoryForNewItem === category;
            return <div key={category} className="bg-[#1c1917] border border-stone-800 rounded-xl overflow-hidden" data-id="element-429">
                    {/* Category Header */}
                    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-stone-800 bg-stone-900/50 flex justify-between items-center sticky top-0 z-10 backdrop-blur-md" data-id="element-430">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-id="element-431">
                        <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" data-id="element-432" />
                        <h2 className="font-bold text-white text-base sm:text-lg truncate" data-id="element-433">
                          {category}
                        </h2>
                        <span className="bg-stone-800 text-stone-400 text-xs px-2 py-0.5 rounded-full font-mono flex-shrink-0" data-id="element-434">
                          {catItems.length}
                        </span>
                      </div>
                      <button onClick={() => startCreateItem(category)} className="text-xs font-bold text-amber-500 hover:text-amber-400 flex items-center gap-1 bg-amber-500/10 px-2 sm:px-3 py-1.5 rounded-lg border border-amber-500/20 transition-colors flex-shrink-0 ml-2" data-id="element-435">
                        <Plus className="w-3 h-3" data-id="element-436" />
                        <span className="hidden sm:inline uppercase tracking-wider" data-id="element-437">
                          Nuevo Plato
                        </span>
                      </button>
                    </div>

                    {/* Add New Item Form */}
                    {isAddingHere && <div className="p-4 sm:p-6 border-b border-stone-800 bg-stone-900/20" data-id="element-438">
                        <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2" data-id="element-439">
                          <Plus className="w-4 h-4 text-amber-500" data-id="element-440" /> Nuevo
                          plato en {category}
                        </h3>
                        <ItemForm formData={itemFormData} setFormData={setItemFormData} onSubmit={handleSaveItem} onCancel={() => setIsCreatingItem(false)} categories={categories} data-id="element-441" />
                      </div>}

                    {/* Items List */}
                    <div className="divide-y divide-stone-800" data-id="element-442">
                      {catItems.map(item => <div key={item.id} draggable={editingItemId !== item.id} onDragStart={() => handleItemDragStart(category, item.id)} onDragOver={e => handleItemDragOver(e, category, item.id)} onDrop={e => handleItemDrop(e, category, item.id)} onDragEnd={handleItemDragEnd} className={`transition-all ${itemDragOver?.category === category && itemDragOver?.id === item.id ? 'bg-amber-500/10 border-l-2 border-amber-500' : itemDragRef.current?.id === item.id ? 'opacity-40' : ''}`} data-id="element-443">
                          {editingItemId === item.id ? <div className="p-4 sm:p-6 bg-stone-800/20" data-id="element-444">
                              <ItemForm formData={itemFormData} setFormData={setItemFormData} onSubmit={handleSaveItem} onCancel={() => setEditingItemId(null)} categories={categories} data-id="element-445" />
                            </div> : <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 cursor-grab active:cursor-grabbing" data-id="element-446">
                              {/* Drag Handle */}
                              <div className="text-stone-700 hover:text-stone-500 flex-shrink-0 touch-none" data-id="element-447">
                                <GripVertical className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-448" />
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0" data-id="element-449">
                                <div className="flex flex-wrap items-center gap-2 mb-0.5" data-id="element-450">
                                  <h3 className={`font-bold text-sm sm:text-base truncate ${item.available ? 'text-stone-200' : 'text-stone-500 line-through'}`} data-id="element-451">
                                    {item.name}
                                  </h3>
                                  <button onClick={() => updateMenuItem(item.id, {
                          available: !item.available
                        })} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border transition-all flex-shrink-0 ${item.available ? 'bg-emerald-900/20 text-emerald-400 border-emerald-900/50 hover:bg-emerald-900/40' : 'bg-red-900/20 text-red-400 border-red-900/50 hover:bg-red-900/40'}`} data-id="element-452">
                                    {item.available ? 'Disponible' : 'Agotado'}
                                  </button>
                                </div>
                                <p className="text-xs sm:text-sm text-stone-500 truncate" data-id="element-453">
                                  {item.description}
                                </p>
                              </div>

                              {/* Price */}
                              <div className="font-mono text-xs sm:text-sm text-stone-300 font-bold flex-shrink-0" data-id="element-454">
                                RD$ {formatPrice(item.price)}
                              </div>

                              {/* Actions */}
                              {deletingItemId === item.id ? <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-455">
                                  <button onClick={() => setDeletingItemId(null)} className="px-2 py-1.5 rounded-md border border-stone-700 text-stone-400 text-xs hover:bg-stone-800" data-id="element-456">
                                    No
                                  </button>
                                  <button onClick={() => {
                        deleteMenuItem(item.id);
                        setDeletingItemId(null);
                      }} className="px-2 py-1.5 rounded-md bg-red-900/20 text-red-400 border border-red-900/50 text-xs font-bold hover:bg-red-900/40" data-id="element-457">
                                    Sí
                                  </button>
                                </div> : <div className="flex items-center gap-1 flex-shrink-0" data-id="element-458">
                                  <button onClick={() => startEditItem(item)} className="p-1.5 sm:p-2 text-stone-500 hover:text-white hover:bg-stone-700 rounded-lg transition-colors" data-id="element-459">
                                    <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-460" />
                                  </button>
                                  <button onClick={() => setDeletingItemId(item.id)} className="p-1.5 sm:p-2 text-stone-500 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-461">
                                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-462" />
                                  </button>
                                </div>}
                            </div>}
                        </div>)}
                      {catItems.length === 0 && !isAddingHere && <div className="px-6 py-10 text-center text-stone-600 text-sm italic" data-id="element-463">
                          No hay platos en esta categoría.
                        </div>}
                    </div>
                  </div>;
          })}

              {groupedItems.length === 0 && <div className="text-center py-20 bg-[#1c1917] border border-stone-800 rounded-xl" data-id="element-464">
                  <Search className="w-10 h-10 text-stone-700 mx-auto mb-4" data-id="element-465" />
                  <p className="text-stone-400 font-medium" data-id="element-466">
                    No se encontraron platos
                  </p>
                  <p className="text-stone-600 text-sm mt-1" data-id="element-467">
                    Intenta con otra búsqueda o categoría
                  </p>
                </div>}
            </div>
          </div>}
      </div>
    </div>;
}
function ItemForm({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  categories
}: {
  formData: any;
  setFormData: (data: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  categories: string[];
}) {
  return <form onSubmit={onSubmit} className="space-y-4" data-id="element-468">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-469">
        <div data-id="element-470">
          <label className="block text-xs font-bold text-stone-500 uppercase mb-1.5" data-id="element-471">
            Nombre
          </label>
          <input type="text" required value={formData.name} onChange={e => setFormData({
          ...formData,
          name: e.target.value
        })} className="w-full bg-[#0a0a0a] border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 outline-none focus:ring-1 focus:ring-amber-500" placeholder="Ej. Churrasco..." data-id="element-472" />
        </div>
        <div data-id="element-473">
          <label className="block text-xs font-bold text-stone-500 uppercase mb-1.5" data-id="element-474">
            Precio (RD$)
          </label>
          <input type="number" required step="0.01" value={formData.price} onChange={e => setFormData({
          ...formData,
          price: parseFloat(e.target.value)
        })} className="w-full bg-[#0a0a0a] border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 outline-none focus:ring-1 focus:ring-amber-500" data-id="element-475" />
        </div>
      </div>

      <div data-id="element-476">
        <label className="block text-xs font-bold text-stone-500 uppercase mb-1.5" data-id="element-477">
          Descripción
        </label>
        <textarea value={formData.description} onChange={e => setFormData({
        ...formData,
        description: e.target.value
      })} className="w-full bg-[#0a0a0a] border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 outline-none h-20 resize-none focus:ring-1 focus:ring-amber-500" placeholder="Descripción del plato..." data-id="element-478" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-479">
        <div data-id="element-480">
          <label className="block text-xs font-bold text-stone-500 uppercase mb-1.5" data-id="element-481">
            Categoría
          </label>
          <select value={formData.category} onChange={e => setFormData({
          ...formData,
          category: e.target.value
        })} className="w-full bg-[#0a0a0a] border border-stone-800 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 outline-none focus:ring-1 focus:ring-amber-500" data-id="element-482">
            {categories.map(cat => <option key={cat} value={cat} data-id="element-483">
                {cat}
              </option>)}
          </select>
        </div>
        <div className="flex items-end pb-2" data-id="element-484">
          <label className="flex items-center gap-2 cursor-pointer select-none group" data-id="element-485">
            <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${formData.available ? 'bg-amber-500 border-amber-500' : 'bg-transparent border-stone-600'}`} data-id="element-486">
              {formData.available && <Check className="w-3.5 h-3.5 text-black" data-id="element-487" />}
            </div>
            <input type="checkbox" className="hidden" checked={formData.available} onChange={e => setFormData({
            ...formData,
            available: e.target.checked
          })} data-id="element-488" />
            <span className="text-sm font-medium text-stone-300 group-hover:text-white transition-colors" data-id="element-489">
              Disponible para ordenar
            </span>
          </label>
        </div>
      </div>

      <div className="flex gap-3 pt-2" data-id="element-490">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-stone-700 text-stone-400 text-sm font-medium hover:bg-stone-800 hover:text-white transition-colors" data-id="element-491">
          Cancelar
        </button>
        <button type="submit" className="px-4 py-2 rounded-lg bg-white text-black text-sm font-bold hover:bg-stone-200 transition-colors flex items-center gap-2" data-id="element-492">
          <Save className="w-4 h-4" data-id="element-493" /> Guardar
        </button>
      </div>
    </form>;
}